import React, { lazy, Suspense, useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import initializeApp from './app/init';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import SuspenseContent from './containers/SuspenseContent';
import { PITCH_MODUS } from './app/pitchModus';
import { APP_ROLLEN } from './utils/appZugang';

// Importing pages
const Layout = lazy(() => import('./containers/Layout'));
const OnboardingWizard = lazy(() => import('./features/onboarding/OnboardingWizard'));
const Einladung = lazy(() => import('./pages/Einladung'));
const Abzeichnen = lazy(() => import('./pages/Abzeichnen'));
const KontoEinrichten = lazy(() => import('./pages/KontoEinrichten'));
const Datenschutz = lazy(() => import('./pages/Datenschutz'));

// Initializing different libraries
initializeApp();

function KeinBetrieb({ email, onSignOut }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
      <div className="max-w-sm w-full rounded-xl border bg-white p-6 text-center space-y-3">
        <p className="font-semibold">Kein Betrieb zugeordnet</p>
        <p className="text-sm text-muted-foreground">{email}</p>
        <button className="text-sm underline" onClick={onSignOut}>Abmelden</button>
      </div>
    </div>
  );
}

// Mitarbeiter-Konten gibt es nur für die iPhone-App
function NurApp({ onSignOut }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
      <div className="max-w-sm w-full rounded-xl border bg-white p-6 text-center space-y-3">
        <p className="font-semibold">Zugang über die HeyOptero-App</p>
        <button className="text-sm underline" onClick={onSignOut}>Abmelden</button>
      </div>
    </div>
  );
}

function AppContent() {
  const { user, loading, passwordRecovery, settingsLoaded, betrieb, signOut, istOpteroTeam, offeneTexte, setOffeneTexte } = useAuth();
  const workspaceMode = useSelector(state => state.settings.workspaceMode);
  const onboarding = useSelector(state => state.settings.onboarding);

  // Lokal überschreibbar, damit der Abschluss des Wizards sofort greift und
  // nicht auf den Redux-Roundtrip nach dem Speichern warten muss.
  const [onboardingErledigt, setOnboardingErledigt] = useState(false);
  useEffect(() => { if (!user) setOnboardingErledigt(false); }, [user]);
  const [passwortGesetzt, setPasswortGesetzt] = useState(false);

  if (loading) return <SuspenseContent />;
  if (passwordRecovery) return <ResetPassword />;
  if (!user) return <Login />;
  if (!settingsLoaded) return <SuspenseContent />;
  if (!betrieb) return <KeinBetrieb email={user.email} onSignOut={signOut} />;
  if (APP_ROLLEN.includes(betrieb.rolle)) return <NurApp onSignOut={signOut} />;

  // Neuer Inhaber aus der Einladung: Passwort + Nutzungsbedingungen vor dem Onboarding
  const passwortFehlt = user.user_metadata?.passwort_fehlt === true && !passwortGesetzt && !istOpteroTeam;
  if (passwortFehlt || offeneTexte.length > 0) {
    return (
      <Suspense fallback={<SuspenseContent />}>
        <KontoEinrichten user={user} betrieb={betrieb} passwortFehlt={passwortFehlt} texte={offeneTexte}
          onSignOut={signOut}
          onFertig={() => { setPasswortGesetzt(true); setOffeneTexte([]); }} />
      </Suspense>
    );
  }

  // Der Wizard läuft nur im Live-Modus. Im Präsentationsmodus sind die
  // Seed-Daten bereits eingerichtet — dort würde er nur im Weg stehen.
  // Im Pitch-Modus bleibt er grundsätzlich aus, egal was in der DB steht.
  const brauchtOnboarding =
    !PITCH_MODUS && workspaceMode === 'live' && !onboarding?.completed && !onboardingErledigt;

  if (brauchtOnboarding) {
    return (
      <Suspense fallback={<SuspenseContent />}>
        <OnboardingWizard onDone={() => setOnboardingErledigt(true)} />
      </Suspense>
    );
  }

  return (
    <Router>
      <Suspense fallback={<SuspenseContent />}>
        <Routes>
          <Route path="/app/*" element={<Layout />} />
          <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

// optero.app/e/<code>: Einladung in die App — öffentlich, ohne Login
const einladungsCode = window.location.pathname.match(/^\/e\/([A-Za-z0-9-]{8,9})\/?$/)?.[1]?.replace('-', '').toUpperCase();

// optero.app/u/<token>: Bericht abzeichnen — öffentlich, ohne Login
const abzeichnenToken = window.location.pathname.match(/^\/u\/([0-9a-f]{32})\/?$/i)?.[1]?.toLowerCase();

// optero.app/datenschutz: öffentlich, ohne Login
const istDatenschutz = /^\/datenschutz\/?$/.test(window.location.pathname);

function App() {
  if (istDatenschutz) {
    return <Suspense fallback={<SuspenseContent />}><Datenschutz /></Suspense>;
  }
  if (einladungsCode) {
    return <Suspense fallback={<SuspenseContent />}><Einladung code={einladungsCode} /></Suspense>;
  }
  if (abzeichnenToken) {
    return <Suspense fallback={<SuspenseContent />}><Abzeichnen token={abzeichnenToken} /></Suspense>;
  }
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
